import {
	type ChildProcess,
	type ChildProcessByStdio,
	spawn as nodeSpawn,
	spawnSync as nodeSpawnSync,
	type SpawnOptions,
	type SpawnOptionsWithStdioTuple,
	type SpawnSyncOptionsWithStringEncoding,
	type SpawnSyncReturns,
	type StdioNull,
	type StdioPipe,
} from "node:child_process";
import { constants as osConstants } from "node:os";
import { basename } from "node:path";
import type { Readable } from "node:stream";
import crossSpawn from "cross-spawn";
import { ATOMIC_AI_AGENT } from "./agent-attribution.ts";

const EXIT_STDIO_IDLE_GRACE_MS = 100;
const EXIT_STDIO_ACTIVE_DRAIN_HARD_CAP_MS = 5_000;
const WINDOWS_EXIT_POLL_INTERVAL_MS = 50;
const WINDOWS_EXIT_CODE_GRACE_MS = 1_000;

/** Build the environment for every Atomic child process. */
export { ATOMIC_AI_AGENT } from "./agent-attribution.ts";
export function createChildProcessEnvironment(
	overrides?: NodeJS.ProcessEnv,
	baseEnv: NodeJS.ProcessEnv = process.env,
): NodeJS.ProcessEnv {
	return { ...baseEnv, ...overrides, AI_AGENT: ATOMIC_AI_AGENT };
}

type WaitForChildProcessOptions = {
	platform?: NodeJS.Platform;
	isWindowsProcessAlive?: (pid: number) => boolean;
	windowsExitPollIntervalMs?: number;
	windowsExitCodeGraceMs?: number;
};

const WINDOWS_SHELL_COMMANDS = new Set(["bun", "npm", "npx", "pnpm", "yarn", "yarnpkg", "corepack"]);

export function shouldUseWindowsShell(command: string): boolean {
	if (process.platform !== "win32") return false;
	const commandName = basename(command).toLowerCase();
	return commandName.endsWith(".cmd") || commandName.endsWith(".bat") || WINDOWS_SHELL_COMMANDS.has(commandName);
}

export function spawnProcess(
	command: string,
	args: string[],
	options: SpawnOptionsWithStdioTuple<StdioNull, StdioPipe, StdioPipe>,
): ChildProcessByStdio<null, Readable, Readable>;
export function spawnProcess(command: string, args: string[], options: SpawnOptions): ChildProcess;
export function spawnProcess(command: string, args: string[], options: SpawnOptions): ChildProcess {
	const childOptions = {
		...options,
		env: options.env ? createChildProcessEnvironment(undefined, options.env) : createChildProcessEnvironment(),
	};
	return process.platform === "win32"
		? crossSpawn(command, args, childOptions)
		: nodeSpawn(command, args, childOptions);
}

export function spawnProcessSync(
	command: string,
	args: string[],
	options: SpawnSyncOptionsWithStringEncoding,
): SpawnSyncReturns<string> {
	const childOptions = {
		...options,
		env: options.env ? createChildProcessEnvironment(undefined, options.env) : createChildProcessEnvironment(),
	};
	return process.platform === "win32"
		? crossSpawn.sync(command, args, childOptions)
		: nodeSpawnSync(command, args, childOptions);
}

export function isWindowsProcessAlive(pid: number): boolean {
	try {
		process.kill(pid, 0);
		return true;
	} catch (error) {
		return (error as NodeJS.ErrnoException).code !== "ESRCH";
	}
}

function mapSignalExitCode(
	code: number | null,
	signal: NodeJS.Signals | null,
	platform: NodeJS.Platform,
): number | null {
	if (code !== null || signal === null || platform === "win32") return code;
	const signalNumber = osConstants.signals[signal] ?? 0;
	return 128 + signalNumber;
}

/**
 * Wait for a child process to terminate without hanging on inherited stdio handles.
 *
 * A short-lived child can `exit` while a detached descendant keeps its stdout/stderr
 * pipe open. We must not resolve and destroy the streams on a short fixed deadline
 * measured from `exit`, or finite post-exit output still being written past that
 * deadline is silently lost (earendil-works/pi#5303). Instead, after `exit` we wait
 * for the pipes to fall idle: the grace timer is re-armed on every chunk, so finite
 * post-exit writers drain while active, while a quiet inherited handle (e.g. a
 * Windows daemonized descendant that never lets `close` fire) releases promptly
 * after the grace elapses. A longer active-drain hard cap is armed once on `exit`
 * so an endlessly noisy descendant cannot keep the wait pending forever.
 */
export function waitForChildProcess(
	child: ChildProcess,
	options: WaitForChildProcessOptions = {},
): Promise<number | null> {
	return new Promise((resolve, reject) => {
		let settled = false;
		let exited = false;
		let exitCode: number | null = null;
		let postExitIdleTimer: NodeJS.Timeout | undefined;
		let postExitActiveDrainHardCapTimer: NodeJS.Timeout | undefined;
		let stdoutEnded = child.stdout === null;
		let stderrEnded = child.stderr === null;
		let windowsExitPoll: NodeJS.Timeout | undefined;
		let windowsExitCodeGraceTimer: NodeJS.Timeout | undefined;
		const platform = options.platform ?? process.platform;
		const processAlive = options.isWindowsProcessAlive ?? isWindowsProcessAlive;
		const windowsExitPollIntervalMs = options.windowsExitPollIntervalMs ?? WINDOWS_EXIT_POLL_INTERVAL_MS;
		const windowsExitCodeGraceMs = options.windowsExitCodeGraceMs ?? WINDOWS_EXIT_CODE_GRACE_MS;

		const cleanup = () => {
			if (windowsExitPoll) {
				clearInterval(windowsExitPoll);
				windowsExitPoll = undefined;
			}
			if (postExitIdleTimer) {
				clearTimeout(postExitIdleTimer);
				postExitIdleTimer = undefined;
			}
			if (postExitActiveDrainHardCapTimer) {
				clearTimeout(postExitActiveDrainHardCapTimer);
				postExitActiveDrainHardCapTimer = undefined;
			}
			if (windowsExitCodeGraceTimer) {
				clearTimeout(windowsExitCodeGraceTimer);
				windowsExitCodeGraceTimer = undefined;
			}
			child.removeListener("error", onError);
			child.removeListener("exit", onExit);
			child.removeListener("close", onClose);
			child.stdout?.removeListener("end", onStdoutEnd);
			child.stderr?.removeListener("end", onStderrEnd);
			child.stdout?.removeListener("data", onData);
			child.stderr?.removeListener("data", onData);
		};

		const finalize = (code: number | null) => {
			if (settled) return;
			settled = true;
			cleanup();
			child.stdout?.destroy();
			child.stderr?.destroy();
			resolve(code);
		};

		const maybeFinalizeAfterExit = () => {
			if (!exited || settled) return;
			if (stdoutEnded && stderrEnded) {
				finalize(exitCode);
			}
		};

		const armIdleTimer = () => {
			if (postExitIdleTimer) clearTimeout(postExitIdleTimer);
			postExitIdleTimer = setTimeout(() => finalize(exitCode), EXIT_STDIO_IDLE_GRACE_MS);
		};

		const armActiveDrainHardCapTimer = () => {
			if (postExitActiveDrainHardCapTimer) return;
			postExitActiveDrainHardCapTimer = setTimeout(() => finalize(exitCode), EXIT_STDIO_ACTIVE_DRAIN_HARD_CAP_MS);
		};

		const onData = () => {
			// Output is still arriving after exit; keep extending the idle grace
			// so active finite writers can drain without truncating the tail.
			if (exited && !settled) armIdleTimer();
		};

		const onStdoutEnd = () => {
			stdoutEnded = true;
			maybeFinalizeAfterExit();
		};

		const onStderrEnd = () => {
			stderrEnded = true;
			maybeFinalizeAfterExit();
		};

		const onError = (err: Error) => {
			if (settled) return;
			settled = true;
			cleanup();
			reject(err);
		};
		const onExit = (code: number | null, signal: NodeJS.Signals | null) => {
			if (windowsExitCodeGraceTimer) {
				clearTimeout(windowsExitCodeGraceTimer);
				windowsExitCodeGraceTimer = undefined;
			}
			exited = true;
			exitCode = mapSignalExitCode(code, signal, platform);
			maybeFinalizeAfterExit();
			if (!settled) {
				armActiveDrainHardCapTimer();
				armIdleTimer();
			}
		};

		const armWindowsExitCodeGraceTimer = () => {
			if (windowsExitCodeGraceTimer) return;
			if (windowsExitPoll) {
				clearInterval(windowsExitPoll);
				windowsExitPoll = undefined;
			}
			windowsExitCodeGraceTimer = setTimeout(() => {
				windowsExitCodeGraceTimer = undefined;
				onExit(child.exitCode ?? 0, null);
			}, windowsExitCodeGraceMs);
		};

		const pollWindowsExit = () => {
			if (platform !== "win32" || !child.pid || exited || settled) return;
			if (!processAlive(child.pid)) {
				if (child.exitCode !== null) {
					onExit(child.exitCode, null);
				} else {
					armWindowsExitCodeGraceTimer();
				}
			}
		};

		const onClose = (code: number | null, signal: NodeJS.Signals | null) => {
			finalize(mapSignalExitCode(code, signal, platform));
		};

		if (platform === "win32" && child.pid) {
			windowsExitPoll = setInterval(pollWindowsExit, windowsExitPollIntervalMs);
		}
		child.stdout?.once("end", onStdoutEnd);
		child.stderr?.once("end", onStderrEnd);
		child.stdout?.on("data", onData);
		child.stderr?.on("data", onData);
		child.once("error", onError);
		child.once("exit", onExit);
		child.once("close", onClose);
	});
}
